import _ from "lodash";
import moment from "moment";
import React, { useState } from "react";

import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { isAuthorized } from "@/utils/authToken";
import { providerRequests } from "@/api/(requests)/provider";

import Button from "../../../UI/Button";
import { durationToString } from "../../../../utils/momentUtils";
import images from "../../../../utils/images";

import "../styles.scss";

const RemoteVideo = (props) => {
  const {
    allottedTime,
    callDetails,
    endCall,
    fullscreenMode,
    note,
    participantDisconnected,
    redirectToMessageProviderDialog,
    remoteMedia,
    remoteTracks,
    timer,
    toggleAudioBtn,
    toggleVideoBtn,
    workflow,
  } = props;
  const isUserPatient = isAuthorized("user:patient");
  const [showEndCallModal, setShowEndCallModal] = useState(false);
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [dimensions, setDimensions] = React.useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });
  const existingNoShowReason = note ? note.content.patient_no_show_reason : "";
  const [noShowReason, setNoShowReason] = React.useState(existingNoShowReason);

  React.useEffect(() => {
    function handleResize() {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    }

    window.addEventListener("resize", handleResize);

    return (_) => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  React.useLayoutEffect(() => {
    props.attachRemoteTracks();
  }, []);

  const updateNoteToBeNoShow = async () => {
    if (workflow.details.showNoShowModalOnly) {
      const noShowArray = _.get(note, "content.patient_no_show_array");

      const no_show = {
        call_id: callDetails.callId,
        patient_no_show: true,
        patient_no_show_reason: noShowReason,
        patient_no_show_time: moment.utc().format(),
        provider_id: callDetails.provider_username,
      };

      if (!noShowArray) {
        note.content.patient_no_show_array = [no_show];
      } else {
        if (
          note.content.patient_no_show_array.find(
            (element) => element.call_id === callDetails.callId,
          )
        ) {
          return;
        }

        note.content.patient_no_show_array.push(no_show);
      }
    } else {
      note.is_billable = false;
      note.content.patient_no_show = true;
      note.content.patient_no_show_reason = noShowReason;
    }

    try {
      const urlParams = { patientId: callDetails.patient_username };

      return providerRequests.put_progress_note({
        context: { showLoader: false },
        data: note,
        urlParams,
      });
    } catch (e) {
      captureErrorMessage(parseError({ error: e, fallback: "Failed to save no-show note" }), {
        tags: {
          component: "RemoteVideo",
          errorType: "submit_no_show_note",
          patientId: callDetails.patient_username,
        },
      });
    }
  };

  let remote = props.remoteVideoRef.current;
  let placeholder = props.remoteVideoPlaceholderRef.current;
  let timerStyle = {};
  let buttonGrpStyle = {};

  let endCallBtn = (
    <button className="GuidedVideoCall__controls-btn GuidedVideoCall__controls-btn--endcall">
      <img
        alt="endCall"
        className="mx-3 d-block cursor"
        onClick={() => {
          if (isUserPatient || workflow.details.skipEndCallModal) {
            if (remoteTracks.length === 0 && !participantDisconnected) {
              redirectToMessageProviderDialog();

              return;
            }

            endCall();

            return;
          }
          if (workflow.details.showNoShowModalOnly) {
            setShowNoShowModal(true);

            return;
          }
          setShowEndCallModal(true);
        }}
        src={images("./icons/end-call-white.svg")}
        style={{ "margin-top": "5px", width: "65px" }}
      />
    </button>
  );

  let additional;

  if (remoteTracks.length === 0 && !participantDisconnected) {
    if (isUserPatient) {
      additional = (
        <div className="GuidedVideoCall__awaiting_message order-1 order-lg-12 text-white fs-18 tw:font-bold">
          <p>
            <span className="fs-20">
              <strong>Awaiting your session to start</strong>
            </span>
            <span>Please hang tight while the other party joins.</span>
          </p>
        </div>
      );
    } else {
      additional =
        workflow["details"] && workflow["details"]["skipEndCallModal"] ? (
          <div className="GuidedVideoCall__awaiting_message order-1 order-lg-12 text-white fs-18 tw:font-bold">
            <p>
              <span className="fs-20 mb-2">
                <strong>Awaiting the patient to join.</strong>
              </span>
              <span className="fs-14 mb-2 tw:font-normal">
                Please hang tight while the other party joins. If the patient does not join, please
                end the call
              </span>
            </p>
          </div>
        ) : (
          <div className="GuidedVideoCall__awaiting_message order-1 order-lg-12 text-white fs-18 tw:font-bold">
            <p>
              <span className="fs-14 mb-2 tw:font-normal">
                Please hang tight while the other party joins. If the patient does not join
              </span>
              <span className="fs-14 tw:font-normal">
                end the call below and select the patient did not attend
              </span>
            </p>
          </div>
        );
    }
  }

  let allottedDuration = moment.duration(allottedTime, "minutes");

  return (
    <>
      <div className="GuidedVideoCall__timer" style={timerStyle}>
        {timer} / <span style={{ color: "#BACED7" }}>{durationToString(allottedDuration)}</span>
      </div>

      <div className="GuidedVideoCall__remote m-auto">
        {remoteTracks.length > 0 ? (
          remoteMedia
        ) : (
          <div
            className="GuidedVideoCall__remote_placeholder m-auto"
            ref={props.remoteVideoPlaceholderRef}
          />
        )}
      </div>

      <div
        className="GuidedVideoCall__controls d-flex d-lg-flex align-items-center tw:justify-center"
        style={buttonGrpStyle}
      >
        <div className="GuidedVideoCall__controls-inner d-flex">
          {toggleAudioBtn}
          {endCallBtn}
          {toggleVideoBtn}
        </div>
      </div>
      {showEndCallModal && (
        <div className="GuidedVideoCall__launcher_modal">
          <div className="GuidedVideoCall__launcher_modal-card">
            <img
              alt="close"
              className="GuidedVideoCall__close-btn"
              onClick={() => setShowEndCallModal(false)}
              role="button"
              src={images("./icons/cross.svg")}
            />
            <p className="launcher-title">End Call</p>
            <div className="GuidedVideoCall__launcher_modal-card-body">
              <div className="left-body">
                <div>
                  <p className="mb-4 tw:font-bold">Review Draft Note</p>
                  <p className="text">
                    You will be able to review the summary of the note and save it as a draft or
                    sign and submit.
                  </p>
                </div>
                <div className="buttons-container">
                  <Button className="Btn Btn--sec review-note" onClick={endCall} type="button">
                    <img className="review-note-icon" src={images("./icons/check-in-icon.svg")} />
                    Review Note
                  </Button>
                  <p className="small-text pt-1">
                    This will exit the call and save a draft of your session
                  </p>
                </div>
              </div>
              <div className="right-body">
                <div>
                  <p className="mb-4 tw:font-bold">Patient Did Not Attend</p>
                  <p className="text mb-0">
                    If your patient did not attend the call you can end the call here. Recording a{" "}
                    <b>No-Show</b> on the session.
                  </p>
                  <input
                    className="my-2"
                    onChange={(e) => setNoShowReason(e.target.value)}
                    placeholder="No Show Reason"
                    type="text"
                    value={noShowReason}
                  />
                </div>
                <div className="buttons-container">
                  <Button
                    className="Btn Btn--err"
                    onClick={() => {
                      updateNoteToBeNoShow().then(() => {
                        props.history.push({
                          pathname: `/app/patient/${callDetails.patient_username}/profile/v2?selectedTab=Notes`,
                        });
                      });
                    }}
                    type="button"
                  >
                    <img
                      src={images("./icons/white-x-circle.svg")}
                      style={{ "margin-right": "10px" }}
                    />
                    No-Show
                  </Button>
                  <p className="small-text pt-1">This will exit the call and record a no show</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showNoShowModal && (
        <div className="GuidedVideoCall__launcher_modal">
          <div className="GuidedVideoCall__launcher_modal-card">
            <img
              alt="close"
              className="GuidedVideoCall__close-btn"
              onClick={() => setShowNoShowModal(false)}
              role="button"
              src={images("./icons/cross.svg")}
            />
            <p className="launcher-title">End Call</p>
            <div className="GuidedVideoCall__launcher_modal-card-body">
              <div className="left-body">
                <div>
                  <p className="mb-4 tw:font-bold">End Call</p>
                  <p className="text">
                    Once the call is complete, please complete the Initial Clinical Assessment for
                    the member
                  </p>
                </div>
                <div className="buttons-container">
                  <Button className="Btn Btn--sec review-note" onClick={endCall} type="button">
                    <img className="review-note-icon" src={images("./icons/check-in-icon.svg")} />
                    End Call
                  </Button>
                  <p className="small-text pt-1">This will exit the call</p>
                </div>
              </div>
              <div className="right-body">
                <div>
                  <p className="mb-4 tw:font-bold">Patient Did Not Attend</p>
                  <p className="text mb-0">
                    If your patient did not attend the call you can end the call here. Recording a{" "}
                    <b>No-Show</b> on the session.
                  </p>
                  <input
                    className="my-2"
                    onChange={(e) => setNoShowReason(e.target.value)}
                    placeholder="No Show Reason"
                    type="text"
                    value={noShowReason}
                  />
                </div>
                <div className="buttons-container">
                  <Button
                    className="Btn Btn--err"
                    onClick={() => {
                      updateNoteToBeNoShow().then(() => {
                        props.history.push({
                          pathname: `/app/patient/${callDetails.patient_username}/profile/v2?selectedTab=Notes`,
                        });
                      });
                    }}
                    type="button"
                  >
                    <img
                      src={images("./icons/white-x-circle.svg")}
                      style={{ "margin-right": "10px" }}
                    />
                    No-Show
                  </Button>
                  <p className="small-text pt-1">This will exit the call and record a no show</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {additional}

      <div className="GuidedVideoCall__mobile_warning d-block d-md-none d-lg-none text-white">
        ⓘ Looking for the full experience? Log in via desktop.
      </div>
    </>
  );
};

export default React.memo(RemoteVideo);
